<template>
  <div>
    <b-row>
      <b-col class="mt-2">
        <b-link @click="toggleDetails()">
          <b>{{ formatString(job.complaint, false).toUpperCase() }}</b>
          <p class="mt-1">
            <b>{{ job.subtotal | currency }}</b>
          </p>
        </b-link>
      </b-col>
      <div class="mt-1">
        <!-- <b>{{ job.subtotal | currency }}</b> -->
        <b-button
          v-if="submitted || isPreApprovedJob"
          class="mt-4 mb-2"
          :class="$smallScreen ? 'ml-1' : 'ml-4'"
          :variant="getJobOperationStatusVariant(job.jobOperationStatusId)"
        >
          {{ getJobOperationStatusNameById(job.jobOperationStatusId) }}
        </b-button>
        <b-dropdown
          v-else
          class="mb-2 mr-6"
          :text="getJobOperationStatusNameById(job.jobOperationStatusId)"
          size="sm"
          :variant="getJobOperationStatusVariant(job.jobOperationStatusId)"
          link-class="text-white"
          menu-class="p-0"
        >
          <b-dropdown-item
            v-for="status in jobOperationStatusesMap"
            :key="status.jobOperationStatusId"
            :class="`bg-${status.config.variant} py-1`"
            link-class="text-white"
            @click="updateOperationStatus(job, status.jobOperationStatusId)"
          >
            {{ status.jobOperationStatusName }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-row>

    <div v-if="showDetails" class="estimate-link-line-item-details">
      <div class="col-head">COMPLAINT</div>

      <div>
        {{ expandedFields.complaint ? job.complaint : formatString(job.complaint, true) }}
        <b-link
          v-if="isStringTruncated(job.complaint) && !expandedFields.complaint"
          class="ml-2"
          @click="expandedFields.complaint = true"
        >
          More
        </b-link>
        <b-link
          v-if="expandedFields.complaint && isStringTruncated(job.complaint)"
          class="ml-2"
          @click="expandedFields.complaint = false"
        >
          Less
        </b-link>
      </div>

      <div class="col-head mt-2">CAUSE</div>
      <div>
        {{ expandedFields.cause ? job.cause : formatString(job.cause, true) }}
        <b-link
          v-if="isStringTruncated(job.cause) && !expandedFields.cause"
          class="ml-2"
          @click="expandedFields.cause = true"
        >
          Show More
        </b-link>
        <b-link
          v-if="expandedFields.cause && isStringTruncated(job.cause)"
          class="ml-2"
          @click="expandedFields.cause = false"
        >
          Show Less
        </b-link>
      </div>

      <div class="col-head mt-2">CORRECTION</div>
      <div>
        {{ expandedFields.correction ? job.correction : formatString(job.correction, true) }}
        <b-link
          v-if="isStringTruncated(job.correction) && !expandedFields.correction"
          class="ml-2"
          @click="expandedFields.correction = true"
        >
          Show More
        </b-link>
        <b-link
          v-if="expandedFields.correction && isStringTruncated(job.correction)"
          class="ml-2"
          @click="expandedFields.correction = false"
        >
          Show Less
        </b-link>
      </div>
      <b-col class="mt-2">
        <div v-if="labor != 0">
          <b-button class="p-0 mr-1 mt-neg4px" variant="link" size="xsm" @click="toggleDetails('Labor')">
            <font-awesome-icon v-if="!showDetailsLabor" icon="plus" class="text-primary" />
            <font-awesome-icon v-if="showDetailsLabor" icon="minus" class="text-primary" />
          </b-button>
          <span class="estimate-link-line-item-subheader">
            LABOR:
            <span class="ml-2">{{ labor | currency }}</span>
          </span>
          <div v-if="showDetailsLabor">
            <div v-for="line in laborLineDetails" :key="line.description" class="ml-4">
              <span>
                <em>{{ line.description }}</em>
              </span>
            </div>
          </div>
        </div>
        <div v-if="parts != 0">
          <b-button class="p-0 mr-1 mt-neg4px" variant="link" size="xsm" @click="toggleDetails('Parts')">
            <font-awesome-icon v-if="!showDetailsParts" icon="plus" class="text-primary" />
            <font-awesome-icon v-if="showDetailsParts" icon="minus" class="text-primary" />
          </b-button>
          <span class="estimate-link-line-item-subheader">
            PARTS:
            <span class="ml-2">{{ parts | currency }}</span>
          </span>
          <div v-if="showDetailsParts">
            <div v-for="line in partsLineDetails" :key="line.description" class="ml-4">
              <span>
                <em>
                  {{ line.partNumber }} | {{ line.description }} | {{ line.price | currency }} | ({{
                    line.quantityRequested
                  }})
                </em>
                <hr v-if="$smallScreen" style="border: 1px dashed; margin-top: -0.05rem; margin-bottom: -0.05rem" />
              </span>
            </div>
          </div>
        </div>
        <div v-if="misc != 0">
          <b-button class="p-0 mr-1 mt-neg4px" variant="link" size="xsm" @click="toggleDetails('Misc')">
            <font-awesome-icon v-if="!showDetailsMisc" icon="plus" class="text-primary" />
            <font-awesome-icon v-if="showDetailsMisc" icon="minus" class="text-primary" />
          </b-button>
          <span class="estimate-link-line-item-subheader">
            MISC:
            <span class="ml-2">{{ misc | currency }}</span>
          </span>
          <div v-if="showDetailsMisc">
            <div v-for="line in miscLineDetails" :key="line.description" class="ml-4">
              <span class="mr-4">
                <em>{{ line.description }} {{ line.price | currency }}</em>
              </span>
            </div>
          </div>
        </div>
        <div v-if="sublet != 0">
          <b-button class="p-0 mr-1 mt-neg4px" variant="link" size="xsm" @click="toggleDetails('Sublet')">
            <font-awesome-icon v-if="!showDetailsSublet" icon="plus" class="text-primary" />
            <font-awesome-icon v-if="showDetailsSublet" icon="minus" class="text-primary" />
          </b-button>
          <span class="estimate-link-line-item-subheader">
            SUBLET:
            <span class="ml-2">{{ sublet | currency }}</span>
          </span>
          <div v-if="showDetailsSublet">
            <div v-for="line in subletLineDetails" :key="line.description" class="ml-4">
              <span class="mr-4">
                <em>{{ line.description }} {{ line.price | currency }}</em>
              </span>
            </div>
          </div>
        </div>
      </b-col>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex';
import { GuidLinkGetters, GuidLinkMutations } from '@/shared/store/guid-link/types';

export default {
  name: 'EstimateLinkLineItem',
  props: {
    job: {
      type: Object,
      required: true
    },
    submitted: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      showDetails: false,
      showDetailsMisc: false,
      showDetailsParts: false,
      showDetailsLabor: false,
      showDetailsSublet: false,
      isPreApprovedJob: false,
      expandedFields: {
        complaint: false,
        cause: false,
        correction: false
      }
    };
  },
  computed: {
    ...mapGetters({ jobOperationStatusesMap: GuidLinkGetters.GET_ESTIMATE_LINK_STATUS_MAP }),
    jobKey() {
      return JSON.stringify({ jobId: this.job.jobId, operationId: this.job.operationId });
    },
    parts() {
      let partsPrice = 0;
      this.job.details.parts?.forEach(pt => (partsPrice += pt.price * pt.quantityRequested));
      return partsPrice;
    },
    labor() {
      let labor = 0;
      this.job.details.laborLines?.forEach(lbr => (labor += lbr.price * lbr.hoursBill));
      return labor;
    },
    misc() {
      let miscPrice = 0;
      this.job.details.miscLines?.forEach(mis => (miscPrice += mis.price * mis.quantity));
      return miscPrice;
    },
    sublet() {
      let subletPrice = 0;
      this.job.details.subletLines?.forEach(sub => (subletPrice += sub.price * sub.quantity));
      return subletPrice;
    },
    miscLineDetails() {
      let details = [];
      this.job.details.miscLines?.forEach(mis => {
        details.push({ description: mis.description, price: mis.price * mis.quantity });
      });
      return details;
    },
    partsLineDetails() {
      let details = [];
      this.job.details.parts?.forEach(line => {
        var suffix = '';
        if (line.partNumber.endsWith('-CORE')) {
          suffix = '-CORE';
        }

        details.push({
          partNumber: line.partNumber,
          description: line.description + suffix,
          quantityRequested: line.quantityRequested,
          price: line.price
        });
      });
      return details;
    },
    laborLineDetails() {
      let details = [];
      this.job.details.laborLines?.forEach(line => {
        details.push({ description: line.description });
      });
      return details;
    },
    subletLineDetails() {
      let details = [];
      this.job.details.subletLines?.forEach(sub => {
        details.push({ description: sub.description, price: sub.price * sub.quantity });
      });
      return details;
    }
  },
  created() {
    if (this.getJobOperationStatusNameById(this.job.jobOperationStatusId) == 'Approved') {
      this.isPreApprovedJob = true;
    }
  },
  methods: {
    ...mapMutations([GuidLinkMutations.SET_OPERATION_STATUS]),
    getJobOperationStatusNameById(jobOperationStatusId) {
      const jobOpStatus = this.jobOperationStatusesMap[jobOperationStatusId];
      return jobOpStatus.jobOperationStatusName;
    },
    getJobOperationStatusVariant(jobOperationStatusId) {
      const jobOpStatus = this.jobOperationStatusesMap[jobOperationStatusId];
      return jobOpStatus.config.variant;
    },
    toggleDetails(value) {
      if (!value) {
        this.showDetails = !this.showDetails;
      } else {
        const propertyName = `showDetails${value}`;
        this[propertyName] = !this[propertyName];
      }
    },
    updateOperationStatus(job, jobOperationStatusId) {
      this[GuidLinkMutations.SET_OPERATION_STATUS]({ job: job, jobOperationStatusId: jobOperationStatusId });
    },
    isStringTruncated(item) {
      return item?.length > 100;
    },
    formatString(item, isJobDetail) {
      var length = isJobDetail ? 100 : this.$smallScreen ? 60 : 100;
      var clamp = clamp || '...';
      return item?.length > length ? item.slice(0, length) + clamp : item;
    }
  }
};
</script>
